import React from 'react';
import { cn } from '@/src/infrastructure/lib/utils';

export interface LiquidToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  id?: string;
  disabled?: boolean;
}

/**
 * Liquid-style toggle switch. The thumb uses an elastic scale transition to
 * give a fluid, spring-like feel without any JS animation library.
 */
export function LiquidToggle({ checked, onChange, label, description, id, disabled }: LiquidToggleProps) {
  return (
    <label
      className={cn('flex cursor-pointer items-center justify-between gap-4', disabled && 'cursor-not-allowed opacity-50')}
      htmlFor={id}
    >
      {(label || description) && (
        <span className="min-w-0 flex-1 select-none">
          {label && <span className="block text-sm font-semibold text-on-surface">{label}</span>}
          {description && <span className="mt-0.5 block text-xs text-on-surface-variant">{description}</span>}
        </span>
      )}

      {/* track */}
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative flex h-7 w-[52px] flex-shrink-0 items-center rounded-full transition-colors duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-primary/30',
          checked ? 'bg-primary shadow-md shadow-primary/30' : 'bg-outline-variant/40',
          disabled && 'cursor-not-allowed',
        )}
      >
        {/* liquid highlight on active */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute inset-0 rounded-full transition-opacity duration-300',
            checked ? 'opacity-100' : 'opacity-0',
          )}
          style={{ background: 'radial-gradient(circle at 25% 40%, rgba(255,255,255,0.18) 0%, transparent 65%)' }}
        />

        {/* thumb — elastic scale on transition */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-1 h-5 rounded-full bg-white shadow-sm',
            'transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
            checked
              ? 'left-6 w-5 scale-100'
              : 'left-1 w-5 scale-95',
          )}
        />
      </button>
    </label>
  );
}
